
import {QCheckbox, QFile, QInput, QSelect} from 'quasar';
import {computed, defineComponent, PropType, ref} from 'vue';
import ApiPasswordInput from 'src/components/qutils/form/ApiPasswordInput.vue';

export interface FieldOption {
  value: string;
  display_name: string;
}

export interface Field {
  type: string;
  required: boolean;
  read_only: boolean;
  label: string;
  children?: Array<Field>;
  choices?: Array<FieldOption>;
  max_length: number;
  help_text: string;
  default?: any
}

const TYPE_MAPPING = {
  field: undefined,
  boolean: {
    component: QCheckbox,
    attrs: {},
  },
  string: {
    component: QInput,
    attrs: {
      type: 'text',
    },
  },
  url: {
    component: QInput,
    attrs: {
      type: 'url',
    },
  },
  email: {
    component: QInput,
    attrs: {
      type: 'email',
    },
  },
  password: {
    component: ApiPasswordInput,
    attrs: {
      type: 'password',
    },
  },
  regex: {
    component: QInput,
  },
  slug: {
    component: QInput,
  },
  integer: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  float: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  decimal: {
    component: QInput,
    attrs: {
      type: 'number',
    },
  },
  date: {
    component: QInput,
    attrs: {
      type: 'date',
    },
  },
  datetime: {
    component: QInput,
    attrs: {
      type: 'date',
    },
  },
  time: {
    component: QInput,
    attrs: {
      type: 'time',
    },
  },
  choice: {
    component: QSelect,
    attrs: {
      'emit-value': true,
      'map-options': true,
    }
  },
  'multiple choice': {
    component: undefined,
  },
  'file upload': {
    component: QFile,
  },
  'image upload': {
    component: QFile,
  },
  list: {
    component: undefined,
  },
  'nested object': {
    component: undefined,
  },
};

export default defineComponent({
  name: 'DynamicInput',
  props: {
    field: {
      required: false,
      type: Object as PropType<Field>,
    },
    fieldComponent: {
      default: undefined,
      type: Object,
    },
    errors: {
      default: undefined,
    },
    modelValue: {
      required: true,
    },
    isFilterable: {
      default: false,
      type: Boolean
    }
  },
  emits: ['update:modelValue'],
  setup(props, ctx) {
    const _options = props?.field?.choices?.flatMap((obj) => {
      return {
        label: obj['display_name'],
        value: obj['value'],
      }
    }) ?? []

    const options = ref<any>(_options);

    const component = computed(() => {
      if (props.fieldComponent) {
        return props.fieldComponent;
      }
      if (!props.field) {
        console.error('no field given');
        return {}
      }
      if (!props.field?.type) {
        console.error('no field type given')
        return {}
      }
      if (!TYPE_MAPPING[props.field.type]) {
        console.error(`Field "${props.field.type}" has no component in mapping.`)
        return {}
      }

      let r = {
        component: TYPE_MAPPING[props.field.type]['component'],
        attrs: {
          ...TYPE_MAPPING[props.field.type]['attrs'],
          label: props.field.label,
          required: props.field.required,
          hint: props.field.help_text,
          readonly: props.field.read_only,
          maxlength: props.field.max_length,
          behavior: 'menu',
          modelValue: props.modelValue ?? props.field.default,
          ...ctx.attrs,
        },
        handlers: {},
      };

      if (r.attrs.required) {
        r['attrs']['label'] = r['attrs']['label'] + ' *';
      }

      if (r['component'] === QSelect) {
        if (props.isFilterable) {
          r.attrs['filterFn'] = (val, update) => {
            if (val === '') {
              update(() => {
                options.value = _options
              })
              return
            }

            update(() => {
              const needle = val.toLowerCase()
              options.value = _options.filter(v => v.label.toLowerCase().indexOf(needle) > -1)
            }, ref => {
              if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1) // reset optionIndex in case there is something selected
                ref.moveOptionSelection(1, true) // focus the first selectable option and do not update the input-value
              }
            })
          }

          r.attrs['use-input'] = true
          r.attrs['clearable'] = true
          r.attrs['required'] = false
        }

        if (r.attrs['modelValue'] === undefined) {
          r.attrs['modelValue'] = null
        }
      }

      if (r['component'] === QCheckbox) {
        r.attrs['class'] = !r.attrs?.class ? 'q-py-sm' : r.attrs.class;
        if (ctx.attrs['no-indeterminate']) {
          r.attrs['modelValue'] = r.attrs['modelValue'] || false;
        }
      }

      r.attrs = {
        ...r.attrs,
        ...ctx.attrs,
      };

      return r;
    });


    return {
      component,
      options,
    };
  },

  computed: {
    QSelect() {
      return QSelect
    }
  },
});
